/**
 * Everything you can see and nothing you can hit: the decor list.
 *
 * Excluded from the fingerprint by construction - if a thing in here needs to
 * stop somebody, it needs an invisible solid beside it. See
 * `src/config/AGENTS.md`.
 */

import { decor, type DecorSpec } from '../world/Level';
import { BARN_H, WALL_H } from './levelDimensions';
import { BEMARI_Z, CABLE_X, TOLPPA_BACK_Z, TOLPPA_Z } from './levelAnsalanka';
import { BAARI_PARTS, TUOPPIPOYTA_PARTS } from './levelBaari';
import { COMPUTERS, LAN_HALL_EXTENSION, PROJEKTORI, VALKOKANGAS } from './levelJuhlasali';
import {
  AMPARI,
  KIMMO_BED_X,
  MATTRESS_TOP,
  POKAALIT,
  ROOMMATE_BED_X,
  bedding,
} from './levelMakuuhuone';
import {
  kolmasSanky,
  lipasto,
  seinakaappi,
} from './levelPirtinKaapit';
import { RISTEYKSEN_OVET, WC_SISUSTUS } from './levelNavetanOvi';
import { HATCH_X, HATCH_Z, KAMIINA, KLAPITELINE, POYTARYHMA } from './levelPalju';
import {
  KAYTAVA,
  KAYTAVAN_VARUSTEIDEN_SAUMAT,
  PIRTTI_ROUTE,
  kaytavanSankyDecor,
  pirtinIkkuna,
} from './levelPirtti';
import { NAVETTA_WINDOWS, TORNIN_OVI } from './levelPorukka';
import { SAUNAN_OVI, buildPenkki } from './levelSaliVaki';
import { DJ_PARTS, SAVUKONE_PARTS, TELTAN_KORIT, TELTAN_STROBOT } from './levelTeltta';

/* ================================================================= decor == */

export const decorItems: DecorSpec[] = [
  // Roofs (visual only, so the buildings read as buildings from outside).
  decor('pirtti-katto', 'pirttiRoof', -0.4, -6.4, 10.4, 4.4, 0.25, WALL_H),
  decor('ulkohuussi-katto', 'pirttiRoof', 10.75, -6.35, 12.45, -4.65, 0.18, 2.3),
  decor('halkokatos-katto', 'pirttiRoof', 15.2, -6.9, 19.2, -4.1, 0.22, 2.4),
  // The clothesline between its two posts, at a height nobody runs into.
  decor('pyykkinaru', 'cable', 21.5, -10.45, 28.9, -10.35, 0.03, 1.95),
  decor('pyykkinaru-piha', 'cable', 36.15, -17.6, 36.25, -14.0, 0.03, 1.95),
  // The window in the pirtti's south wall, and the sash that came out of it.
  ...pirtinIkkuna(),
  decor('kaytava-katto', 'pirttiRoof', 10.0, 0.0, 33.4, KAYTAVA.northZ + 0.4, 0.25, WALL_H),
  decor('navetta-katto', 'barnRoof', 125.5, -45.6, 155.9, -29.5, 0.35, BARN_H),
  decor('porstua-katto', 'barnRoof', 128.7, -30.2, 131.4, -27.2, 0.2, 2.8),
  decor('keittio-katto', 'lanRoof', 119.2, -37.1, 126.2, -32.0, 0.25, 3.0),
  decor('aitat-katto', 'pirttiRoof', 45.6, 7.6, 54.4, 15.4, 0.25, 2.8),
  decor('lato-katto', 'barnRoof', 41.6, -30.4, 56.4, -20.6, 0.35, 6.6),
  decor('heinalato-katto', 'barnRoof', 14.6, -32.9, 25.4, -25.1, 0.3, 4.6),
  decor('konehalli-katto', 'pirttiRoof', 63.6, -34.4, 74.4, -27.6, 0.3, 4.2),
  decor('vaarin-pirtti-katto', 'pirttiRoof', 69.6, 5.6, 82.4, 15.4, 0.3, 4.2),

  // Cars and campers.

  // Ansalanka: the row on the grass, and the charging kit that gives the
  // obstacle its name. Everything here is visual - the cable on the ground and
  // the tolppa are the only two colliders in the set-piece.
  decor(
    'ansalanka-pistorasia',
    'furniture',
    CABLE_X - 0.16,
    TOLPPA_Z - 0.1,
    CABLE_X + 0.16,
    TOLPPA_Z + 0.14,
    0.3,
    0.82,
  ),
  // The slack, dropped at the foot of the tolppa. It used to lie behind the
  // post, which is where a coil goes when there is nothing behind the post;
  // there is a car parked there now, so it is beside it instead.
  decor(
    'ansalanka-johtokiepp',
    'cable',
    CABLE_X + 0.28,
    TOLPPA_BACK_Z + 0.03,
    CABLE_X + 1.12,
    TOLPPA_Z - 0.03,
    0.12,
  ),
  // Two lights that say the thing is live: one on the tolppa, one on the flap.
  decor(
    'ansalanka-tolpan-valo',
    'screen',
    CABLE_X - 0.05,
    TOLPPA_Z + 0.14,
    CABLE_X + 0.05,
    TOLPPA_Z + 0.18,
    0.06,
    1.0,
  ),
  decor('ansalanka-kyltin-tolppa', 'wood', 103.16, -14.26, 103.26, -14.14, 1.34),
  decor(
    'ansalanka-latausluukku',
    'screen',
    CABLE_X - 0.11,
    BEMARI_Z - 0.06,
    CABLE_X + 0.11,
    BEMARI_Z,
    0.1,
    0.42,
  ),

  /* ---- JUHLANAVETAN JULKISIVU ---- */
  //
  // Four windows and a red-boarded end, because a thirty-metre blank wall is
  // the one thing the real building is not. All of it sits a few centimetres
  // proud of the wall so nothing z-fights with the surface behind it.
  ...NAVETTA_WINDOWS,
  // The red plank end of the barn, and the red panelling on the porch.
  decor('navetta-punainen-paaty', 'faluRed', 147.9, -29.72, 155.75, -29.62, BARN_H - 0.15),
  // The porch, painted the way the real one is: white round the mouth, red
  // panelling low on the sides. Nothing across the opening - that is the door.
  // Everything here overlaps the surface it sits on by a centimetre or two:
  // decor flush with a wall face z-fights with it.
  decor('porstua-pieli-lansi', 'porcelain', 128.85, -27.44, 129.2, -27.3, 2.6),
  decor('porstua-pieli-ita', 'porcelain', 130.9, -27.44, 131.25, -27.3, 2.6),
  decor('porstua-panelointi-lansi', 'faluRed', 128.76, -29.0, 128.86, -27.5, 0.95, 0.2),
  decor('porstua-panelointi-ita', 'faluRed', 131.24, -29.0, 131.34, -27.5, 0.95, 0.2),
  // The flag halyard's cleat end, so the pole is not a floating white line.
  decor('lipputangon-jalusta', 'stone', 141.35, -28.75, 142.25, -27.85, 0.22),

  /* ---- TORNIMAJOITUS: ovi ---- */
  //
  // The way into the silo, on the +z face under the sign that names it - the
  // side the polku comes up to, and the side the sofa was dragged out of.
  //
  // Decor only, like the keittiö's leaf and for the same reason: the drum's
  // collider is the box inscribed in it, so there is no room behind this to
  // walk into. It is a face on a building, not a way through one.
  //
  // The drum is a 20-gon, so the wall falls away from the tangent point by
  // seven centimetres over the width of a door. Everything here is set that
  // much proud of the ideal radius, or the leaf would be half-swallowed by the
  // wall at its own hinges.
  ...TORNIN_OVI.decor,

  // And the sauna's, at the far end of the juhlasali: the same construction
  // again, on the one wall in the hall that had nothing on it below the
  // screen. Decor for the same reason those two are - `sali-ita` stays solid
  // whatever anybody does to the handle. See `SAUNA`.
  ...SAUNAN_OVI.decor,

  // Tree canopy.
  decor('puun-latvus', 'foliage', 63.0, 1.0, 70.0, 8.0, 3.0, 4.0),

  // Door headers. Visual only: the player's head reaches 2.43 m at the apex of
  // a jump, and bonking on a lintel mid-sprint would feel terrible.
  decor('huoneen-ovi-kamana', 'pirttiWall', 10.0, PIRTTI_ROUTE.arch.minZ, 10.3, PIRTTI_ROUTE.arch.maxZ, WALL_H - 2.5, 2.5),
  decor('ulko-ovi-kamana', 'pirttiWall', 33.0, 0.9, 33.3, 2.6, WALL_H - 2.15, 2.15),
  decor('navetan-ovi-kamana', 'lanWall', 129.4, -30.0, 130.9, -29.65, 1.3, 2.3),
  ...WC_SISUSTUS.decor,
  ...RISTEYKSEN_OVET.decor,

  // Bedroom dressing.
  ...bedding('kimmo', KIMMO_BED_X),
  ...bedding('nukkuja', ROOMMATE_BED_X),
  // The bed nobody reached, under the name board. Frame and all - see
  // `KOLMAS_SANKY` for why even the frame is decor.
  ...kolmasSanky(),
  // The corridor beds keep full-height colliders, with separate visible frames
  // and mattresses so their top surfaces cannot fight over the same pixels.
  ...kaytavanSankyDecor(),
  ...KAYTAVAN_VARUSTEIDEN_SAUMAT,
  // The rest of the north wall: what the pirtti keeps its own things in, next
  // to the vaatekaappi and over it. Decor for the third bed's reason - see
  // `LIPASTO`.
  ...lipasto(),
  ...seinakaappi(),
  // Kimmo's duvet, where he kicked it on the way out.
  decor('peitto', 'fabric', 1.3, -5.2, 2.05, -3.8, 0.14),
  // The plinths the three cups stand on. See `pokaalit()`.
  ...POKAALIT.decor,

  // The morning after, part two: what he did not make it to the bucket for.
  //
  // Three overlapping patches rather than one box, because the outline is the
  // only thing standing between a stain and a yellow rug. The steps between
  // them are a millimetre each: enough that the tops never z-fight where they
  // overlap, little enough that the sides of the patches do not draw the join
  // as a line. All of them start below the pillow - a stain that runs under it
  // is one nobody can see, and is not where it would have happened anyway.
  decor('patjan-tahra', 'stain', 0.34, -4.52, 0.9, -4.0, 0.01, MATTRESS_TOP),
  decor('patjan-tahra-b', 'stain', 0.46, -4.66, 0.78, -4.48, 0.011, MATTRESS_TOP),
  decor('patjan-tahra-c', 'stain', 0.26, -4.06, 0.56, -3.76, 0.012, MATTRESS_TOP),

  // The bucket's sanka, dropped flat across the rim. A straight bar rather
  // than a bail, because decor is axis-aligned - and because a bucket that has
  // been set down in a hurry has its handle lying over the top anyway.
  decor(
    'oksennusampari-sanka',
    'metal',
    AMPARI.x - 0.19,
    AMPARI.z + 0.03,
    AMPARI.x + 0.19,
    AMPARI.z + 0.06,
    0.016,
    AMPARI.height + 0.02,
  ),
  decor('matto', 'fabric', 3.0, -3.4, 7.0, -0.4, 0.02, 0.01),

  // Kimmo's chair, and a chair for everybody else who is not asleep.
  decor('kimmon-tuoli-istuin', 'goal', 145.4 + LAN_HALL_EXTENSION, -37.45, 146.3 + LAN_HALL_EXTENSION, -36.55, 0.08, 0.55),
  decor('kimmon-tuoli-selka', 'goal', 146.1 + LAN_HALL_EXTENSION, -37.45, 146.3 + LAN_HALL_EXTENSION, -36.55, 0.55, 0.63),
  decor('kimmon-tuoli-jalka', 'metal', 145.72 + LAN_HALL_EXTENSION, -37.15, 145.98 + LAN_HALL_EXTENSION, -36.85, 0.55, 0.0),

  // Sleeping bags (flat, no collision).
  decor('makuupussi-1', 'fabric', 137.2, -38.6, 138.0, -36.6, 0.1, 0.01),
  decor('makuupussi-2', 'fabric', 145.0, -36.4, 145.7, -34.6, 0.1, 0.01),
  decor('makuupussi-3', 'fabric', 146.6 + LAN_HALL_EXTENSION, -39.0, 147.3 + LAN_HALL_EXTENSION, -37.2, 0.1, 0.01),

  // Valkokangas at the back of the hall, per the floor plan. What is on it is
  // `projection` below - this is the sheet it is thrown against.
  decor(
    'valkokangas',
    // A sheet, not a monitor. It used to be drawn in the lit `screen` blue,
    // which was the right guess while nothing was being projected on it - now
    // that something is, the blue was a border round the picture rather than
    // the white margin a projector leaves on a screen it does not fill.
    'porcelain',
    VALKOKANGAS.x,
    VALKOKANGAS.minZ,
    VALKOKANGAS.x + 0.25,
    VALKOKANGAS.maxZ,
    VALKOKANGAS.height,
    VALKOKANGAS.bottom,
  ),

  // And the projector throwing it, hung off the roof over the aisle. Four
  // boxes: the plate on the ceiling, the drop it hangs from, the body, and the
  // lens. The light between the lens and the sheet is not here - it is
  // `projection.beam` below, because it is the picture's own light and goes
  // out with it.
  decor(
    'projektorin-kattolevy',
    'metal',
    PROJEKTORI.x - PROJEKTORI.barrel - 0.28,
    PROJEKTORI.z - 0.12,
    PROJEKTORI.x - PROJEKTORI.barrel - 0.04,
    PROJEKTORI.z + 0.12,
    0.03,
    BARN_H - 0.03,
  ),
  decor(
    'projektorin-varsi',
    'metal',
    PROJEKTORI.x - PROJEKTORI.barrel - 0.19,
    PROJEKTORI.z - 0.03,
    PROJEKTORI.x - PROJEKTORI.barrel - 0.13,
    PROJEKTORI.z + 0.03,
    BARN_H - 0.03 - (PROJEKTORI.y + PROJEKTORI.halfHeight),
    PROJEKTORI.y + PROJEKTORI.halfHeight,
  ),
  decor(
    'projektori',
    'metal',
    PROJEKTORI.x - PROJEKTORI.barrel - PROJEKTORI.depth,
    PROJEKTORI.z - PROJEKTORI.halfWidth,
    PROJEKTORI.x - PROJEKTORI.barrel,
    PROJEKTORI.z + PROJEKTORI.halfWidth,
    PROJEKTORI.halfHeight * 2,
    PROJEKTORI.y - PROJEKTORI.halfHeight,
  ),
  // Drawn in `screen`, which is the level's lit-thing material: unlit, and
  // killed by `setPowerOn`. A lens with a lamp behind it is a light source,
  // and it is on the same mains as every other lit thing in the hall.
  decor(
    'projektorin-linssi',
    'screen',
    PROJEKTORI.x - PROJEKTORI.barrel,
    PROJEKTORI.z - 0.075,
    PROJEKTORI.x,
    PROJEKTORI.z + 0.075,
    0.15,
    PROJEKTORI.y - 0.075,
  ),

  // Yard dressing.
  decor('pyykkinaru', 'fabric', 36.1, -26.0, 36.3, -17.6, 0.05, 2.0),

  // The kamiina's hatch, its kahva, and the light coming round the door.
  //
  // Flat plates laid across a curved face, which stand a few centimetres proud
  // of the firebox at their corners - which is what a hatch and its frame do
  // on a real one too. The glow is its own sliver across the bottom of the
  // door rather than a colour on the door: a hatch that has not shut square
  // since it was new is the only way, from outside a 95 cm rim, to see that
  // there is still a fire in there at all.
  decor(
    'kamiina-luukku',
    'metal',
    HATCH_X - 0.07,
    HATCH_Z,
    HATCH_X + 0.02,
    HATCH_Z + KAMIINA.hatchWidth,
    KAMIINA.hatchHeight,
    KAMIINA.hatchBottom,
  ),
  decor(
    'kamiina-kahva',
    'metal',
    HATCH_X,
    HATCH_Z + KAMIINA.hatchWidth - 0.03,
    HATCH_X + 0.05,
    HATCH_Z + KAMIINA.hatchWidth + 0.05,
    0.035,
    KAMIINA.hatchBottom + 0.09,
  ),
  decor(
    'kamiina-luukun-rako',
    'ember',
    HATCH_X - 0.05,
    HATCH_Z + 0.03,
    HATCH_X + 0.03,
    HATCH_Z + KAMIINA.hatchWidth - 0.03,
    0.03,
    KAMIINA.hatchBottom + 0.015,
  ),
  // The hearth the stove stands on. Concrete slabs, because a wood stove
  // straight onto a wooden terrace is how a venue loses a terrace.
  decor(
    'kamiinan-alusta',
    'stone',
    KAMIINA.x - 0.62,
    KAMIINA.z - 0.62,
    KAMIINA.x + 0.62,
    KAMIINA.z + 0.62,
    0.04,
  ),

  ...KLAPITELINE.decor,
  ...POYTARYHMA.decor,
  ...COMPUTERS.decor,
  ...BAARI_PARTS.decor,
  ...TUOPPIPOYTA_PARTS.decor,
  ...buildPenkki(),

  /* ---- XLMOTO-teltan dj-setti ja sen piha ---- */
  ...DJ_PARTS,
  ...SAVUKONE_PARTS,
  ...TELTAN_KORIT,
  ...TELTAN_STROBOT,
];
